import { lazy } from "react";
import { Route } from "react-router";
import { all_routes } from "./all_routes";
import Login from "../auth/login/login";

/**
 * Pages are loaded on demand.
 *
 * Importing them eagerly put every screen in the app — and every library any
 * one of them uses — into the first download, so opening the login page paid
 * for the whole product. Each page below becomes its own chunk, fetched when
 * its route is first visited. `Login` stays eager because it is the entry
 * screen and would otherwise flash a loader on a cold start.
 *
 * Anything added here must be a default export, which is what `lazy` resolves.
 */
const Register = lazy(() => import("../auth/register/register"));
const TwoStepVerification = lazy(
  () => import("../auth/twoStepVerification/twoStepVerification"),
);
const EmailVerification = lazy(
  () => import("../auth/emailVerification/emailVerification"),
);
const ResetPassword = lazy(() => import("../auth/resetPassword/resetPassword"));
const ForgotPassword = lazy(
  () => import("../auth/forgotPassword/forgotPassword"),
);
const ResetPasswordSuccess = lazy(
  () => import("../auth/resetPasswordSuccess/resetPasswordSuccess"),
);
const LockScreen = lazy(() => import("../auth/lockScreen"));

const Error404 = lazy(() => import("../pages/error/error-404"));
const Error500 = lazy(() => import("../pages/error/error-500"));
const ErrorConnectionTimeout = lazy(
  () => import("../pages/error/error-connection-timeout"),
);

const AdminDashboard = lazy(() => import("../admin/adminDashboard"));
const DashboardItems = lazy(
  () => import("../Nestfin Superadmin/dashboard-items/DashboardItems"),
);

const InventoryList = lazy(
  () => import("../Nestfin Superadmin/properties/InventoryList"),
);
const AllPropertyList = lazy(
  () => import("../Nestfin Superadmin/properties/AllPropertyList"),
);
const EditPropertyDetails = lazy(
  () => import("../Nestfin Superadmin/properties/EditPropertyDetails"),
);
const ViewPropertyImages = lazy(
  () => import("../Nestfin Superadmin/properties/ViewPropertyImages"),
);

const TenantsList = lazy(
  () => import("../Nestfin Superadmin/organization/TenantsList"),
);
const TenantDetailsPage = lazy(
  () => import("../Nestfin Superadmin/organization/TenantDetailsPage"),
);
const BookingList = lazy(
  () => import("../Nestfin Superadmin/organization/BookingList"),
);

const ComplaintsList = lazy(
  () => import("../Nestfin Superadmin/complaints/ComplaintsList"),
);
const ComplaintDetails = lazy(
  () => import("../Nestfin Superadmin/complaints/ComplaintDetails"),
);
const ExpensesList = lazy(
  () => import("../Nestfin Superadmin/expenses/ExpensesList"),
);

const LeadsList = lazy(() => import("../Nestfin Superadmin/leads/LeadsList"));
const LeadsReport = lazy(
  () => import("../Nestfin Superadmin/leads/LeadsReport"),
);
const LeadsManagement = lazy(
  () => import("../Nestfin Superadmin/leads/Leads-management/LeadsManagement"),
);
const UploadLeads = lazy(
  () => import("../Nestfin Superadmin/leads/upload-leads/LeadsUpload"),
);

const SubscriptionNew = lazy(
  () => import("../Nestfin Superadmin/subscription/Subscription_new"),
);
const ExpiredSubscription = lazy(
  () => import("../Nestfin Superadmin/subscription/ExpiredSubscription"),
);
const ActiveSubscription = lazy(
  () => import("../Nestfin Superadmin/subscription/ActiveSubscription"),
);

const PaidCollections = lazy(
  () => import("../Nestfin Superadmin/collection/PaidCollectionsList"),
);
const UnpaidCollections = lazy(
  () => import("../Nestfin Superadmin/collection/UnpaidCollectionsList"),
);

const AllBankList = lazy(
  () => import("../Nestfin Superadmin/per-organization/AllBankList"),
);
const BankTransactions = lazy(
  () => import("../Nestfin Superadmin/per-organization/BankTransactions"),
);
const StaffTransactions = lazy(
  () => import("../Nestfin Superadmin/per-organization/StaffTransactions"),
);
const TransactionReport = lazy(
  () => import("../Nestfin Superadmin/reconciliation/TransactionReport"),
);
const ReconciliationDashboard = lazy(
  () => import("../Nestfin Superadmin/reconciliation/ReconciliationDashboard"),
);

const routes = all_routes;

export const publicRoutes = [
  {
    path: "/",
    name: "Root",
    element: <AdminDashboard />,
    route: Route,
  },
  {
    path: routes.adminDashboard,
    element: <AdminDashboard />,
    // element: <DashboardItems />,
    route: Route,
  },
  {
    path: routes.AllOrganizations,
    element: <AdminDashboard />,
    // element: <DashboardItems />,
    route: Route,
  },
  {
    path: routes.dashboardItems,
    element: <DashboardItems />,
    route: Route,
  },

  // ======== nestfin superadmin ======== //
  {
    path: routes.inventoryList,
    element: <InventoryList />,
    route: Route,
  },
  {
    path: routes.tenants,
    element: <TenantsList />,
    route: Route,
  },
  {
    path: routes.tenantsDetails,
    element: <TenantDetailsPage />,
    route: Route,
  },
  {
    path: routes.bookings,
    element: <BookingList />,
    route: Route,
  },

  {
    path: routes.AllProperty,
    element: <AllPropertyList />,
    route: Route,
  },
  {
    path: routes.EditProperty,
    element: <EditPropertyDetails />,
    route: Route,
  },
  {
    path: routes.viewPropertyImages,
    element: <ViewPropertyImages />,
    route: Route,
  },
  // {
  //   path: routes.propertyLeads,
  //   element: <PropertyLeadsList />,
  //   route: Route,
  // },

  {
    path: routes.complaints,
    element: <ComplaintsList />,
    route: Route,
  },
  {
    path: routes.complainDetails,
    element: <ComplaintDetails />,
    route: Route,
  },
  {
    path: routes.expenses,
    element: <ExpensesList />,
    route: Route,
  },
  {
    path: routes.uploadLeads,
    element: <UploadLeads />,
    route: Route,
  },
  {
    path: routes.leadsList,
    element: <LeadsList />,
    route: Route,
  },
  {
    path: routes.leadsReport,
    element: <LeadsReport />,
    route: Route,
  },
  {
    path: routes.leadsManagement,
    element: <LeadsManagement />,
    route: Route,
  },
  {
    path: routes.subscriptionNew,
    element: <SubscriptionNew />,
    route: Route,
  },
  {
    path: routes.subscriptionExpired,
    element: <ExpiredSubscription />,
    route: Route,
  },
  {
    path: routes.subscriptionActive,
    element: <ActiveSubscription />,
    route: Route,
  },
  {
    path: routes.paidCollection,
    element: <PaidCollections />,
    route: Route,
  },
  {
    path: routes.unPaidCollection,
    element: <UnpaidCollections />,
    route: Route,
  },

  // ------- ------------
  {
    path: routes.allBanks,
    element: <AllBankList />,
    route: Route,
  },
  {
    path: routes.bankTransaction,
    element: <BankTransactions />,
    route: Route,
  },
  {
    path: routes.staffTransaction,
    element: <StaffTransactions />,
    route: Route,
  },
  {
    path: routes.transactionReport,
    element: <TransactionReport />,
    route: Route,
  },
  {
    path: routes.transectionDashboardItems,
    element: <ReconciliationDashboard />,
    route: Route,
  },
];

export const authRoutes = [
  // {
  //   path: routes.comingSoon,
  //   element: <ComingSoon />,
  //   route: Route,
  // },
  {
    path: routes.login,
    element: <Login />,
    route: Route,
  },
  {
    path: routes.register,
    element: <Register />,
    route: Route,
  },
  {
    path: routes.twoStepVerification,
    element: <TwoStepVerification />,
    route: Route,
  },
  {
    path: routes.emailVerification,
    element: <EmailVerification />,
    route: Route,
  },
  {
    path: routes.register,
    element: <Register />,
    route: Route,
  },
  {
    path: routes.resetPassword,
    element: <ResetPassword />,
    route: Route,
  },
  {
    path: routes.forgotPassword,
    element: <ForgotPassword />,
    route: Route,
  },
  {
    path: all_routes.errorTimeout,
    element: <ErrorConnectionTimeout />,
    route: Route,
  },
  {
    path: all_routes.error404,
    element: <Error404 />,
    route: Route,
  },
  {
    path: all_routes.error500,
    element: <Error500 />,
    route: Route,
  },
  // {
  //   path: routes.underMaintenance,
  //   element: <UnderMaintenance />,
  //   route: Route,
  // },
  // {
  //   path: routes.underConstruction,
  //   element: <UnderConstruction />,
  // },
  {
    path: routes.lockScreen,
    element: <LockScreen />,
  },
  {
    path: routes.resetPasswordSuccess,
    element: <ResetPasswordSuccess />,
  },
];
